import { useState } from "react";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import { INCIDENT_FOLDERS, type IncidentFolderId } from "../../lib/constants";

const COLLAPSED_STORAGE_KEY = "beas.incidents.folderSidebar.collapsed";
const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 56;

function readCollapsedFromStorage(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const stored = window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (stored === "0") return false;
    return true;
  } catch {
    return true;
  }
}

function writeCollapsedToStorage(collapsed: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? "1" : "0");
  } catch {
    /* ignore */
  }
}

type IncidentFolderSidebarProps = {
  selectedFolder: IncidentFolderId;
  folderCounts: Record<IncidentFolderId, number | null>;
  onSelectFolder: (folderId: IncidentFolderId) => void;
};

export default function IncidentFolderSidebar({
  selectedFolder,
  folderCounts,
  onSelectFolder,
}: IncidentFolderSidebarProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [collapsed, setCollapsed] = useState(readCollapsedFromStorage);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsedToStorage(next);
      return next;
    });
  };

  const toggleLabel = collapsed ? t("incidents.folders.expand") : t("incidents.folders.collapse");
  const toggleIcon = isMdUp
    ? collapsed
      ? "chevron_right"
      : "chevron_left"
    : collapsed
      ? "expand_more"
      : "expand_less";

  return (
    <Box
      sx={{
        width: { xs: "100%", md: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH },
        flex: { xs: "0 0 auto", md: "0 0 auto" },
        minHeight: 0,
        height: { xs: "auto", md: "100%" },
        maxHeight: "100%",
        alignSelf: "stretch",
        borderRight: { xs: 0, md: 1 },
        borderBottom: { xs: 1, md: 0 },
        borderColor: "divider",
        bgcolor: (paletteTheme) =>
          paletteTheme.palette.mode === "dark"
            ? alpha(paletteTheme.palette.background.default, 0.6)
            : "#f8fafd",
        display: "flex",
        flexDirection: { xs: collapsed ? "row" : "column", md: "column" },
        alignItems: { xs: collapsed ? "center" : "stretch", md: "stretch" },
        overflow: "hidden",
        transition: (sxTheme) =>
          sxTheme.transitions.create("width", {
            easing: sxTheme.transitions.easing.sharp,
            duration: sxTheme.transitions.duration.shorter,
          }),
      }}
    >
      <Box
        sx={{
          px: collapsed ? 0.5 : 2,
          py: 1,
          borderBottom: { xs: collapsed ? 0 : 1, md: 1 },
          borderColor: "divider",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          minHeight: 44,
        }}
      >
        {!collapsed && (
          <Typography
            variant="overline"
            sx={{ fontWeight: 800, letterSpacing: "0.08em", color: "text.secondary" }}
          >
            {t("incidents.folders.title")}
          </Typography>
        )}
        <Tooltip title={toggleLabel} placement={isMdUp ? "right" : "bottom"}>
          <IconButton
            size="small"
            onClick={toggleCollapsed}
            aria-label={toggleLabel}
            aria-expanded={!collapsed}
          >
            <Icon fontSize="small">{toggleIcon}</Icon>
          </IconButton>
        </Tooltip>
      </Box>

      <List
        dense
        disablePadding
        sx={{
          flex: { xs: "0 0 auto", md: 1 },
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          py: 0.5,
          display: { xs: collapsed ? "flex" : "block", md: "block" },
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        {INCIDENT_FOLDERS.map((folder) => {
          const selected = selectedFolder === folder.id;
          const count = folderCounts[folder.id];
          const countLabel = count == null ? "" : String(count);
          const label = t(`incidents.folders.${folder.id}`);
          const hint = t(`incidents.folders.${folder.id}Hint`);

          return (
            <Tooltip
              key={folder.id}
              title={collapsed ? (countLabel ? `${label} (${countLabel})` : label) : ""}
              placement={isMdUp ? "right" : "bottom"}
              disableHoverListener={!collapsed}
            >
              <ListItemButton
                selected={selected}
                onClick={() => onSelectFolder(folder.id)}
                aria-label={countLabel ? `${label} (${countLabel})` : label}
                sx={{
                  mx: collapsed ? 0.5 : 1,
                  my: 0.25,
                  px: collapsed ? 1 : 2,
                  borderRadius: 1.5,
                  py: 0.85,
                  justifyContent: collapsed ? "center" : "flex-start",
                  minWidth: collapsed ? 40 : 0,
                  "&.Mui-selected": {
                    bgcolor: (paletteTheme) => alpha(paletteTheme.palette.primary.main, 0.12),
                    color: "primary.main",
                    "& .MuiListItemIcon-root": { color: "primary.main" },
                    "&:hover": {
                      bgcolor: (paletteTheme) => alpha(paletteTheme.palette.primary.main, 0.16),
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 34,
                    justifyContent: "center",
                    color: "inherit",
                  }}
                >
                  <Icon fontSize="small">{folder.icon}</Icon>
                </ListItemIcon>
                {!collapsed && (
                  <>
                    <ListItemText
                      primary={label}
                      secondary={hint}
                      slotProps={{
                        primary: {
                          variant: "body2",
                          fontWeight: selected ? 700 : 600,
                          noWrap: true,
                        },
                        secondary: {
                          variant: "caption",
                          noWrap: true,
                          sx: { display: { xs: "none", lg: "block" } },
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        minWidth: 24,
                        textAlign: "right",
                        color: selected ? "primary.main" : "text.secondary",
                      }}
                    >
                      {countLabel}
                    </Typography>
                  </>
                )}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );
}
